/**
 * 🛡️ FatSecretBadge — source-of-truth badge for every meal card.
 *
 * Yuri spec 2026-02 Iter. 316: ALWAYS show the macro source so the user is
 * never left guessing whether the data was validated vs estimated. Three
 * possible looks:
 *
 *   - FatSecret Canada   → cyan glow + 🇨🇦
 *   - FatSecret France   → cyan glow + 🇫🇷
 *   - FatSecret Global   → cyan glow + 🌐
 *   - AI estimate        → amber glow + ✨ (Sparkles)
 *
 * The component accepts either an `analysis` or a `meal` prop carrying a
 * `data_source` string. Backward compatible with the old "fatsecret_canada"
 * value (treated as CA).
 *
 * Variants: "full" (default — pill with text) and "mini" (compact icon-only
 * pill suitable for dense lists).
 */
import React from 'react';
import { ShieldCheck, Sparkles, Globe } from 'lucide-react';

// Map a `data_source` value to its UI shape (flag + label + region key).
function classifySource(src) {
  if (!src) return { kind: 'ai' };
  const s = String(src).toLowerCase();
  if (s.includes('fatsecret')) {
    if (s.includes('canada') || s.includes('_ca')) return { kind: 'fatsecret', flag: '🇨🇦', region: 'CA' };
    if (s.includes('france') || s.includes('_fr')) return { kind: 'fatsecret', flag: '🇫🇷', region: 'FR' };
    if (s.includes('global') || s.includes('intl')) return { kind: 'fatsecret', flag: null, region: 'GLOBAL' };
    // Default to Canada when only "fatsecret" is given (legacy data)
    return { kind: 'fatsecret', flag: '🇨🇦', region: 'CA' };
  }
  return { kind: 'ai' };
}

export default function FatSecretBadge({
  analysis,
  meal,
  variant = 'full',
  language = 'fr',
  // showAiBadge kept for backward-compat — defaults to TRUE now (always show source).
  showAiBadge = true,
}) {
  const fr = (language || 'fr').startsWith('fr');
  const src = (analysis || meal || {}).data_source;
  const { kind, flag, region } = classifySource(src);
  const isVerified = kind === 'fatsecret';

  if (!isVerified && !showAiBadge) return null;

  // Tooltip text per kind
  const verifiedTitleFr = region === 'CA' ? 'Macros vérifiées · FatSecret Canada (1.5M+ aliments)'
    : region === 'FR' ? 'Macros vérifiées · FatSecret France'
    : 'Macros vérifiées · FatSecret Global';
  const verifiedTitleEn = region === 'CA' ? 'Macros verified · FatSecret Canada (1.5M+ foods)'
    : region === 'FR' ? 'Macros verified · FatSecret France'
    : 'Macros verified · FatSecret Global';
  const aiTitle = fr
    ? "Estimation IA — l'aliment n'a pas pu être matché en base. La précision reste solide pour les plats simples."
    : "AI estimate — food could not be matched in the database. Precision remains solid on simple meals.";

  // ───── MINI variant ─────
  if (variant === 'mini') {
    if (isVerified) {
      return (
        <span
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-cyan-500/15 ring-1 ring-cyan-400/50"
          style={{ boxShadow: '0 0 8px rgba(34,211,238,0.5)' }}
          title={fr ? verifiedTitleFr : verifiedTitleEn}
          data-testid="fatsecret-badge-mini"
          data-source-region={region}
        >
          <ShieldCheck className="w-2.5 h-2.5 text-cyan-300" />
          <span className="text-[8.5px] font-black uppercase tracking-widest text-cyan-200 leading-none">
            {fr ? 'Vérifié' : 'Verified'}
          </span>
          {flag ? <span className="text-[9px] leading-none" aria-hidden="true">{flag}</span>
                : <Globe className="w-2.5 h-2.5 text-cyan-300" aria-hidden="true" />}
        </span>
      );
    }
    return (
      <span
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-500/15 ring-1 ring-amber-400/45 text-amber-200"
        title={aiTitle}
        data-testid="ai-estimate-badge-mini"
        data-source-region="AI"
      >
        <Sparkles className="w-2.5 h-2.5" />
        <span className="text-[8.5px] font-black uppercase tracking-widest leading-none">
          {fr ? 'IA' : 'AI'}
        </span>
      </span>
    );
  }

  // ───── FULL variant (default) ─────
  if (isVerified) {
    return (
      <span
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-cyan-500/20 to-blue-500/15 ring-1 ring-cyan-400/60"
        style={{ boxShadow: '0 0 10px rgba(34,211,238,0.55)' }}
        title={fr ? verifiedTitleFr : verifiedTitleEn}
        data-testid="fatsecret-badge"
        data-source-region={region}
      >
        <ShieldCheck className="w-3 h-3 text-cyan-200" />
        <span className="text-[9.5px] font-black uppercase tracking-widest text-cyan-100 leading-none">
          {fr ? 'Vérifié FatSecret' : 'FatSecret Verified'}
        </span>
        {flag ? <span className="text-[10px] leading-none" aria-hidden="true">{flag}</span>
              : <Globe className="w-3 h-3 text-cyan-200" aria-hidden="true" />}
      </span>
    );
  }

  // AI estimate — explicit, never hidden by default anymore (Yuri Iter. 316).
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-amber-500/15 to-orange-500/10 ring-1 ring-amber-400/45 text-amber-200"
      style={{ boxShadow: '0 0 8px rgba(251,191,36,0.30)' }}
      title={aiTitle}
      data-testid="ai-estimate-badge"
      data-source-region="AI"
    >
      <Sparkles className="w-3 h-3" />
      <span className="text-[9.5px] font-black uppercase tracking-widest leading-none">
        {fr ? 'Estimation IA' : 'AI estimate'}
      </span>
    </span>
  );
}
